import { AssetRequestType, AssetResolver, AssetResolverContext } from "@/core/asset-resolver"
import { Texture } from "@/core/backends/webgl/texture"
import { Renderer } from "@/core/pipeline/renderer"
import { Mask, MaskDetectionState } from "@/core/types"
import { imageDataToBitmap } from "@/core/utils/image-data"
import { clamp, radians } from "@/core/utils/math"
import { isObjectMask } from "@/core/utils/masks"
import { AbortError, isAbortError } from "@/utils/error"
import { PubSub } from "@/utils/pub-sub"
import { mat4, vec2, vec4 } from "gl-matrix"
import PQueue from "p-queue"
import { Photo } from "@/core/photo"
import type { AIMattingDetection } from "@/lib/ai/detection/ai-detection"
import { Sentry } from "@/telemetry/sentry"
import { toInitialOrientation } from "@/lib/ai/alignment/ai-image-align"
import type { MaskCache } from "@/storage/mask-cache"
import { AIPeopleMaskProvider, type AIPeopleMaskService } from "./ai-mask-people-provider"
import { AIStandardMaskProvider } from "./ai-mask-standard-provider"
import { AIMaskNotDetectedError, type MaskResult } from "./ai-mask-result"

export interface AIMaskResolverEvent {
	type:
		| "detection-started"
		| "detection-succeeded"
		| "detection-canceled"
		| "detection-failed"
		| "detection-coordinates-changed"
	payload?: Mask
}

export interface MaskTextureLease {
	texture: Texture
	release: () => void
}

interface TextureLeaseEntry {
	promise: Promise<Texture | undefined>
	consumers: number
	controller: AbortController
	texture?: Texture
	deleted: boolean
}

interface CommittedTextureRequest {
	promise: Promise<void>
	controller: AbortController
}

export class AIMaskResolver extends AssetResolver {
	events = new PubSub<AIMaskResolverEvent>()

	maskDetectionState: Record<string, MaskDetectionState> = {}
	private readonly standardProvider: AIStandardMaskProvider
	private readonly peopleProvider: AIPeopleMaskProvider
	// Concurrent consumers share a texture entry; the final lease release deletes its GPU texture.
	private textureLeases = new Map<string, TextureLeaseEntry>()
	// Committed artefacts retain their own leases until explicit removal or resolver cleanup.
	// Transient previews can therefore release independently after normal asset resolution.
	private committedTextureLeases = new Map<string, MaskTextureLease>()
	private committedTextureRequests = new Map<string, CommittedTextureRequest>()
	// Detection status supports the current photo's UI and is cleared when the resolver is cleaned up.
	private detectedCache = new Map<string, boolean>()
	private activeDetectionControllers = new Set<AbortController>()
	private queue = new PQueue({ concurrency: 1 })
	private generation = 0

	private scratchTexture?: Texture

	private renderer?: Renderer

	private label: string

	public constructor(proxy: AIMattingDetection, label: string = "main", cache?: MaskCache) {
		super()
		this.label = label
		const renderAnalysisInput = (photo: Photo, size: vec2) =>
			this.renderAnalysisInput(photo, size)
		this.standardProvider = new AIStandardMaskProvider(proxy, renderAnalysisInput, cache)
		this.peopleProvider = new AIPeopleMaskProvider(proxy, renderAnalysisInput, cache, (mask) =>
			this.events.publish({ type: "detection-coordinates-changed", payload: mask }),
		)

		this.queue.on("error", (e) => Sentry.captureException(e))
	}

	public get type(): AssetRequestType {
		return "ai-mask"
	}

	public get people(): AIPeopleMaskService {
		return this.peopleProvider
	}

	public prepare(renderer: Renderer) {
		this.renderer = renderer
		this.scratchTexture = renderer.backend.createTexture({
			label: "aiMaskResolver.scratchTexture",
			format: "r32float",
		})
	}

	public resolve(id: string, context: AssetResolverContext) {
		const resolved = this.committedTextureLeases.get(id)
		if (resolved) {
			context.renderer.assets.setArtefact({ type: this.type, id }, resolved.texture)
			return Promise.resolve()
		}

		const pending = this.committedTextureRequests.get(id)
		if (pending) {
			return pending.promise
		}

		const controller = new AbortController()
		const request = this.resolveCommittedTexture(id, context, controller.signal).finally(() => {
			if (this.committedTextureRequests.get(id)?.promise === request) {
				this.committedTextureRequests.delete(id)
			}
		})
		this.committedTextureRequests.set(id, { promise: request, controller })
		return request
	}

	private async resolveCommittedTexture(
		id: string,
		context: AssetResolverContext,
		signal: AbortSignal,
	) {
		const photo = context.photo
		const mask = photo.adjustments.localAdjustments
			?.flatMap((localAdjustment) => localAdjustment.masks)
			.find((mask) => mask.id === id)

		if (!mask) {
			console.warn(`[ai-mask-resolver:${this.label}] Cannot find specified mask with id`, id)
			return
		}

		try {
			const lease = await this.acquireMaskTexture(photo, mask, signal)
			if (signal.aborted) {
				lease.release()
				return
			}
			const previous = this.committedTextureLeases.get(id)
			this.committedTextureLeases.set(id, lease)
			context.renderer.assets.setArtefact({ type: this.type, id }, lease.texture)
			previous?.release()
			this.setMaskDetectionState(mask, "succeeded")
		} catch (error) {
			if (isAbortError(error)) {
				return
			}
			if (error instanceof AIMaskNotDetectedError) {
				this.detectedCache.set(this.getMaskTextureId(photo, mask) ?? id, false)
				context.renderer.assets.setArtefact(
					{ type: this.type, id },
					{ error: "Not detected" },
				)
			}
			this.setMaskDetectionState(mask, "failed")
		}
	}

	public releaseCommittedMask(id: string) {
		const request = this.committedTextureRequests.get(id)
		request?.controller.abort()
		if (this.committedTextureRequests.get(id) === request) {
			this.committedTextureRequests.delete(id)
		}

		this.committedTextureLeases.get(id)?.release()
		this.committedTextureLeases.delete(id)
		this.renderer?.assets.removeArtefact({ type: this.type, id })
		delete this.maskDetectionState[id]
	}

	public cleanup() {
		this.generation += 1
		this.activeDetectionControllers.forEach((controller) => controller.abort())
		this.activeDetectionControllers.clear()
		this.committedTextureRequests.forEach((request) => request.controller.abort())
		this.committedTextureLeases.forEach((lease) => lease.release())
		this.committedTextureLeases.clear()
		this.committedTextureRequests.clear()
		this.textureLeases.forEach((entry) => {
			entry.deleted = true
			entry.texture?.delete()
		})
		this.textureLeases.clear()
		this.detectedCache.clear()
		this.standardProvider.cleanup()
		this.peopleProvider.cleanup()
		this.maskDetectionState = {}
	}

	public async detectMask(photo: Photo, mask: Mask) {
		const controller = new AbortController()
		const generation = this.generation
		const id = this.getMaskTextureId(photo, mask)
		if (!id || this.hasMaskDetected(photo, mask) === false) {
			return false
		}

		this.activeDetectionControllers.add(controller)
		this.setMaskDetectionState(mask, "started")
		try {
			await this.queue.add(() => this.getObjectMaskResult(photo, mask, controller.signal), {
				signal: controller.signal,
			})
			if (controller.signal.aborted || generation !== this.generation) {
				throw new AbortError("Mask detection belongs to a previous photo")
			}
			this.detectedCache.set(id, true)
			this.setMaskDetectionState(mask, "succeeded")
			return true
		} catch (error) {
			if (controller.signal.aborted || isAbortError(error)) {
				this.setMaskDetectionState(mask, "canceled")
			} else {
				if (error instanceof AIMaskNotDetectedError) {
					this.detectedCache.set(id, false)
				}
				this.setMaskDetectionState(mask, "failed")
			}
			return false
		} finally {
			this.activeDetectionControllers.delete(controller)
		}
	}

	public async acquireMaskTexture(
		photo: Photo,
		mask: Mask,
		signal?: AbortSignal,
	): Promise<MaskTextureLease> {
		signal?.throwIfAborted()
		const id = this.getMaskTextureId(photo, mask)
		if (!id) {
			throw new Error("Cannot create a texture key for mask")
		}

		let entry = this.textureLeases.get(id)
		if (!entry) {
			// Concurrent consumers share texture creation, but each receives its own release obligation.
			const controller = new AbortController()
			const generation = this.generation
			entry = {
				consumers: 0,
				controller,
				deleted: false,
				promise: Promise.resolve(undefined),
			}
			const currentEntry = entry
			this.activeDetectionControllers.add(controller)
			currentEntry.promise = this.queue
				.add(() => this.detectMaskTexture(photo, mask, controller.signal), {
					signal: controller.signal,
				})
				.then((texture) => {
					if (!texture) {
						throw new Error("Could not detect mask")
					}
					if (generation !== this.generation || currentEntry.deleted) {
						texture.delete()
						throw new AbortError("Mask texture belongs to a previous photo")
					}
					currentEntry.texture = texture
					if (currentEntry.consumers === 0) {
						currentEntry.deleted = true
						texture.delete()
						this.textureLeases.delete(id)
					}
					return texture
				})
				.catch((error) => {
					if (this.textureLeases.get(id) === currentEntry) {
						this.textureLeases.delete(id)
					}
					throw error
				})
				.finally(() => this.activeDetectionControllers.delete(controller))
			this.textureLeases.set(id, currentEntry)
		}

		entry.consumers += 1
		let texture: Texture
		let removeAbortListener: (() => void) | undefined
		try {
			const resolvedTexture = signal
				? await Promise.race([
						entry.promise,
						new Promise<never>((_resolve, reject) => {
							const onAbort = () =>
								reject(new AbortError("Mask texture request was canceled"))
							signal.addEventListener("abort", onAbort, { once: true })
							removeAbortListener = () => signal.removeEventListener("abort", onAbort)
							if (signal.aborted) {
								onAbort()
							}
						}),
					])
				: await entry.promise
			if (!resolvedTexture) {
				throw new Error("Could not create mask texture")
			}
			texture = resolvedTexture
			this.detectedCache.set(id, true)
		} catch (error) {
			this.releaseTextureLease(id, entry)
			throw error
		} finally {
			removeAbortListener?.()
		}

		let released = false
		return {
			texture,
			release: () => {
				if (released) {
					return
				}
				released = true
				this.releaseTextureLease(id, entry)
			},
		}
	}

	private releaseTextureLease(id: string, entry: TextureLeaseEntry) {
		entry.consumers = Math.max(0, entry.consumers - 1)
		if (entry.consumers !== 0 || entry.deleted) {
			return
		}
		entry.deleted = true
		if (entry.texture) {
			entry.texture.delete()
		} else {
			entry.controller.abort()
		}
		if (this.textureLeases.get(id) === entry) {
			this.textureLeases.delete(id)
		}
	}

	hasMaskDetected(photo: Photo, mask: Mask): boolean | undefined {
		if (isObjectMask(mask)) {
			const id = this.getMaskTextureId(photo, mask)

			if (!id) {
				return false
			}

			return this.detectedCache.get(id)
		}

		return true
	}

	private setMaskDetectionState(mask: Mask, state: MaskDetectionState) {
		this.maskDetectionState[mask.id] = state

		switch (state) {
			case "started":
				this.events.publish({ type: "detection-started", payload: mask })
				break
			case "succeeded":
				this.events.publish({ type: "detection-succeeded", payload: mask })
				break
			case "canceled":
				this.events.publish({ type: "detection-canceled", payload: mask })
				break
			case "failed":
				this.events.publish({ type: "detection-failed", payload: mask })
		}
	}

	private async detectMaskTexture(photo: Photo, mask: Mask, signal: AbortSignal) {
		const result = await this.getObjectMaskResult(photo, mask, signal)
		signal.throwIfAborted()

		return this.createMaskTexture(photo, result, mask.type === "sky")
	}

	private async getObjectMaskResult(photo: Photo, mask: Mask, signal: AbortSignal) {
		if (mask.type === "people") {
			return await this.peopleProvider.getMaskResult(photo, mask, signal)
		}
		return await this.standardProvider.getMaskResult(photo, mask, signal)
	}

	public async createMaskGroupThumbnail(photo: Photo, masks: Mask[]) {
		if (typeof document === "undefined" || typeof ImageData === "undefined") {
			return undefined
		}

		const thumbnail = getThumbnailSize(48, photo.imageState.croppedImageSize)
		const groupMask = new Float32Array(thumbnail.width * thumbnail.height)

		for (const mask of masks) {
			if (!mask.active) {
				continue
			}

			const maskPixels = await this.createThumbnailMask(photo, mask, thumbnail)
			if (!maskPixels) {
				continue
			}

			const blendMode = mask.blendMode
			for (let i = 0; i < groupMask.length; i++) {
				const value = maskPixels[i] ?? 0
				const currentValue = groupMask[i] ?? 0
				groupMask[i] =
					blendMode === "subtract"
						? clamp(currentValue - value, 0, 1)
						: Math.max(currentValue, value)
			}
		}

		return await createBinaryMaskBlob(groupMask, thumbnail.width, thumbnail.height)
	}

	private async createThumbnailMask(photo: Photo, mask: Mask, thumbnail: ThumbnailSize) {
		switch (mask.type) {
			case "subject":
			case "background":
			case "people":
			case "sky": {
				const result = await this.getObjectMaskResult(
					photo,
					mask,
					new AbortController().signal,
				)
				return createObjectThumbnailMask(
					mask,
					result,
					thumbnail,
					photo.imageState.cropMatrixInv,
					photo.initialOrientation,
				)
			}
			case "radial":
				return createRadialThumbnailMask(
					mask,
					thumbnail,
					photo.imageState.originalImageSize,
					photo.imageState.cropMatrixInv,
				)
			case "gradient":
				return createGradientThumbnailMask(
					mask,
					thumbnail,
					photo.imageState.originalImageSize,
					photo.imageState.cropMatrixInv,
				)
			case "brush":
				return createBrushThumbnailMask(
					mask,
					thumbnail,
					photo.imageState.originalImageSize,
					photo.imageState.cropMatrixInv,
				)
		}
	}

	private createMaskTexture(
		photo: Photo,
		result: MaskResult,
		inflation = false,
		reductionFactor = 0,
	) {
		const renderer = this.renderer
		const input = this.scratchTexture

		if (!renderer || !input) {
			return
		}

		input.update({
			format: "r32float",
			width: result.width,
			height: result.height,
			data: result.mask,
		})

		const maskTexture = renderer.backend.createTexture({
			label: "aiMaskResolver.maskTexture",
			format: "rg16float",
			width: result.width >> reductionFactor,
			height: result.height >> reductionFactor,
		})

		// Orient image back to match original image
		const rotation = photo.initialOrientation * 90
		const rotationMatrix = mat4.fromZRotation(mat4.create(), -radians(rotation))

		maskTexture.bindFramebuffer()
		input.bind(0)

		renderer.shaders.viewport.render({
			inputTexture: 0,
			viewMatrix: rotationMatrix,
			filterNearest: false,
		})

		renderer.refineMaskPass.render(photo.texture, maskTexture, inflation)

		return maskTexture
	}

	private async renderAnalysisInput(photo: Photo, size: vec2) {
		const outputTexture = this.scratchTexture

		if (!outputTexture) {
			return
		}

		outputTexture.update({
			width: size[0],
			height: size[1],
			format: "rgba8unorm",
		})

		// Orient image in order for detection to work properly
		const rotation = photo.initialOrientation * 90
		const rotationMatrix = mat4.fromZRotation(mat4.create(), radians(rotation))
		mat4.invert(rotationMatrix, rotationMatrix)

		this.renderer?.renderWithoutAdjustments(photo, {
			width: size[0],
			height: size[1],
			output: { format: "rgba", texture: outputTexture },
			cropMatrix: rotationMatrix,
			colorSpace: "srgb",
			ignoreLensCorrection: true,
		})

		const data = outputTexture.read()

		return await imageDataToBitmap({
			data,
			width: size[0],
			height: size[1],
			colorSpace: "srgb",
		})
	}

	private getMaskTextureId(photo: Photo, mask: Mask) {
		switch (mask.type) {
			case "subject":
			case "background":
				// "background" mask is just subject mask inverted
				return `${photo.id}_subject`
			case "people":
				return this.peopleProvider.getMaskTextureId(photo, mask)

			default:
				// There is only one segmentation mask per type per photo
				return `${photo.id}_${mask.type}`
		}
	}
}

interface ThumbnailSize {
	width: number
	height: number
}

function getThumbnailSize(size: number, imageSize: vec2): ThumbnailSize {
	const imageWidth = Math.max(imageSize[0], 1)
	const imageHeight = Math.max(imageSize[1], 1)
	const aspectRatio = imageWidth / imageHeight
	const width = aspectRatio >= 1 ? size : Math.max(1, Math.round(size * aspectRatio))
	const height = aspectRatio >= 1 ? Math.max(1, Math.round(size / aspectRatio)) : size

	return { width, height }
}

function createObjectThumbnailMask(
	mask: Mask,
	result: MaskResult,
	thumbnail: ThumbnailSize,
	cropMatrix: mat4,
	initialOrientation: number,
) {
	const pixels = new Float32Array(thumbnail.width * thumbnail.height)
	const inverted = mask.type === "background" ? !mask.inverted : mask.inverted

	for (let y = 0; y < thumbnail.height; y++) {
		for (let x = 0; x < thumbnail.width; x++) {
			const sourcePoint = getThumbnailSourcePoint(x, y, thumbnail, cropMatrix)
			if (!sourcePoint) {
				continue
			}
			const detectionPoint = toInitialOrientation(sourcePoint, initialOrientation)
			const sourceX = clamp(Math.floor(detectionPoint[0] * result.width), 0, result.width - 1)
			const sourceY = clamp(
				Math.floor(detectionPoint[1] * result.height),
				0,
				result.height - 1,
			)
			const sourceIndex = sourceY * result.width + sourceX
			const index = y * thumbnail.width + x
			const value = result.mask[sourceIndex] ?? 0
			pixels[index] = inverted ? 1 - value : value
		}
	}

	return pixels
}

function smoothstep(edge0: number, edge1: number, x: number) {
	const t = clamp((x - edge0) / (edge1 - edge0), 0, 1)
	return t * t * (3 - 2 * t)
}

function createRadialThumbnailMask(
	mask: Extract<Mask, { type: "radial" }>,
	thumbnail: ThumbnailSize,
	sourceImageSize: vec2,
	cropMatrix: mat4,
) {
	const pixels = new Float32Array(thumbnail.width * thumbnail.height)
	const imageWidth = Math.max(sourceImageSize[0], 1)
	const imageHeight = Math.max(sourceImageSize[1], 1)
	const angle = radians(mask.angle)
	const cos = Math.cos(angle)
	const sin = Math.sin(angle)
	const blur = Math.min(1 - mask.feather, 0.99)
	const offset = 2 - mask.feather
	const width = Math.max(mask.radiusX * 2, 0.001)
	const height = Math.max(mask.radiusY * 2, 0.001)

	for (let y = 0; y < thumbnail.height; y++) {
		for (let x = 0; x < thumbnail.width; x++) {
			const sourcePoint = getThumbnailSourcePoint(x, y, thumbnail, cropMatrix)
			if (!sourcePoint) {
				continue
			}
			const [px, py] = sourcePoint
			const dx = (px - mask.centerX) * imageWidth
			const dy = (py - mask.centerY) * imageHeight
			const rotatedX = (dx * cos - dy * sin) / imageWidth
			const rotatedY = (dx * sin + dy * cos) / imageHeight
			const distance = Math.hypot((rotatedX / width) * offset, (rotatedY / height) * offset)
			let value = smoothstep(1, blur, distance)
			if (mask.inverted) {
				value = 1 - value
			}
			pixels[y * thumbnail.width + x] = value
		}
	}

	return pixels
}

function createGradientThumbnailMask(
	mask: Extract<Mask, { type: "gradient" }>,
	thumbnail: ThumbnailSize,
	sourceImageSize: vec2,
	cropMatrix: mat4,
) {
	const pixels = new Float32Array(thumbnail.width * thumbnail.height)
	const imageWidth = Math.max(sourceImageSize[0], 1)
	const imageHeight = Math.max(sourceImageSize[1], 1)
	const startX = mask.startX * imageWidth
	const startY = mask.startY * imageHeight
	const endX = mask.endX * imageWidth
	const endY = mask.endY * imageHeight
	const dx = endX - startX
	const dy = endY - startY
	const lengthSquared = Math.max(dx * dx + dy * dy, 0.001)

	for (let y = 0; y < thumbnail.height; y++) {
		for (let x = 0; x < thumbnail.width; x++) {
			const sourcePoint = getThumbnailSourcePoint(x, y, thumbnail, cropMatrix)
			if (!sourcePoint) {
				continue
			}
			const [sourceX, sourceY] = sourcePoint
			const px = sourceX * imageWidth
			const py = sourceY * imageHeight
			const t = ((px - startX) * dx + (py - startY) * dy) / lengthSquared
			let value = clamp(1 - t, 0, 1)
			if (mask.inverted) {
				value = 1 - value
			}
			pixels[y * thumbnail.width + x] = value
		}
	}

	return pixels
}

function createBrushThumbnailMask(
	mask: Extract<Mask, { type: "brush" }>,
	thumbnail: ThumbnailSize,
	sourceImageSize: vec2,
	cropMatrix: mat4,
) {
	const pixels = new Float32Array(thumbnail.width * thumbnail.height)
	const imageWidth = Math.max(sourceImageSize[0], 1)
	const imageHeight = Math.max(sourceImageSize[1], 1)
	const aspectRatio = imageHeight / imageWidth

	for (const stroke of mask.strokes) {
		const feather = Math.max(0.04, stroke.feather)
		const brushSize = stroke.size + stroke.feather * stroke.size
		const halfBrushWidth = aspectRatio > 1 ? brushSize * 0.5 : brushSize * 0.5 * aspectRatio
		const halfBrushHeight = aspectRatio > 1 ? (brushSize * 0.5) / aspectRatio : brushSize * 0.5
		const innerRadius = stroke.size * 0.5 - feather * stroke.size * 0.5
		const outerRadius = stroke.size * 0.5 + feather * stroke.size * 0.5
		const normalizedInnerRadius = innerRadius / (stroke.size + feather * stroke.size)
		const normalizedOuterRadius = outerRadius / (stroke.size + feather * stroke.size)

		for (let i = 0; i < stroke.points.length; i += 2) {
			const pointX = stroke.points[i]
			const pointY = stroke.points[i + 1]
			if (pointX === undefined || pointY === undefined) {
				continue
			}

			for (let y = 0; y < thumbnail.height; y++) {
				for (let x = 0; x < thumbnail.width; x++) {
					const sourcePoint = getThumbnailSourcePoint(x, y, thumbnail, cropMatrix)
					if (!sourcePoint) {
						continue
					}
					const [px, py] = sourcePoint
					const distance = Math.hypot(
						(px - pointX) / Math.max(halfBrushWidth * 2, 0.001),
						(py - pointY) / Math.max(halfBrushHeight * 2, 0.001),
					)
					const edge = smoothstep(normalizedInnerRadius, normalizedOuterRadius, distance)
					const alpha = clamp(1 - edge, 0, 1) * stroke.flow
					const index = y * thumbnail.width + x
					const currentValue = pixels[index] ?? 0
					const value = clamp(stroke.density * alpha, 0, 1)
					pixels[index] = stroke.isErasing
						? currentValue * (1 - alpha)
						: clamp(value + currentValue * (1 - alpha), 0, 1)
				}
			}
		}
	}

	if (mask.inverted) {
		for (let i = 0; i < pixels.length; i++) {
			pixels[i] = 1 - (pixels[i] ?? 0)
		}
	}

	return pixels
}

function getThumbnailSourcePoint(
	x: number,
	y: number,
	thumbnail: ThumbnailSize,
	cropMatrix: mat4,
): vec2 | undefined {
	const point = vec4.fromValues(
		((x + 0.5) / thumbnail.width) * 2 - 1,
		((y + 0.5) / thumbnail.height) * 2 - 1,
		0,
		1,
	)
	vec4.transformMat4(point, point, cropMatrix)

	const w = point[3] || 1
	const sourceX = (point[0] / w) * 0.5 + 0.5
	const sourceY = (point[1] / w) * 0.5 + 0.5

	if (sourceX < 0 || sourceX > 1 || sourceY < 0 || sourceY > 1) {
		return undefined
	}

	return [sourceX, sourceY]
}

async function createBinaryMaskBlob(mask: Float32Array, width: number, height: number) {
	const imageData = new ImageData(width, height)
	for (let i = 0; i < mask.length; i++) {
		const maskValue = mask[i] ?? 0
		const value = Math.round(clamp(maskValue, 0, 1) * 255)
		const offset = i * 4
		imageData.data[offset] = value
		imageData.data[offset + 1] = value
		imageData.data[offset + 2] = value
		imageData.data[offset + 3] = 255
	}

	const canvas = document.createElement("canvas")
	canvas.width = width
	canvas.height = height
	const context = canvas.getContext("2d")
	if (!context) {
		return undefined
	}

	context.putImageData(imageData, 0, 0)
	return await new Promise<Blob | undefined>((resolve) => {
		canvas.toBlob((blob) => resolve(blob ?? undefined), "image/png")
	})
}
